import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Drawer } from 'antd';
import './inboundSlideOut.scss';
import { loadFeatureSettings, verifyFeatureAccess } from '../../../global/featureFlags';
import CallFollowUpPanel from './CallFollowUpPanel';
import ResultDeliveryPanel from './ResultDeliveryPanel';
import {
  VoiceCallContextTitle,
  VoiceCallRecording,
  VoiceCallSid,
  VoiceCallSummary,
  VoiceCallTranscript,
  getDepartmentName,
} from '../VoiceCallContext';

const InboundSlideOut = ({
  open,
  onClose,
  callLog,
  setCallIndex,
  disableNext,
  disablePrevious,
  isNavigationLoading,
  departments,
  showDepartment,
  onCallLogUpdated,
  timestampField,
}) => {
  const width = window.innerWidth > 768 ? 900 : '100%';
  const [canUseCallFollowups, setCanUseCallFollowups] = useState(null);

  // Cleanup: pause all audio elements when slideout closes or when navigating to a different call
  useEffect(() => {
    const audioElements = document.querySelectorAll('.inbound-slide-out audio');
    audioElements.forEach((audio) => {
      audio.pause();
    });
  }, [open, callLog?.id]);

  useEffect(() => {
    if (!open) {
      setCanUseCallFollowups(null);
      return undefined;
    }

    let active = true;
    setCanUseCallFollowups(null);
    // Operational auto-create orgs work follow-ups exclusively from the Follow Up tab.
    Promise.all([
      verifyFeatureAccess('call_followups'),
      loadFeatureSettings('call_followups_auto_create'),
    ])
      .then(([hasFollowUps, autoCreateSettings]) => {
        if (active) setCanUseCallFollowups(!!hasFollowUps && !autoCreateSettings?.enabled);
      })
      .catch(() => {
        if (active) setCanUseCallFollowups(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  const title = (
    <VoiceCallContextTitle
      callLog={callLog}
      variant="inbound"
      timestampField={timestampField}
      includeDuration
      showDepartment={showDepartment}
      departments={departments}
      onNavigatePrevious={() => setCallIndex('previous')}
      onNavigateNext={() => setCallIndex('next')}
      onClose={onClose}
      canNavigatePrevious={!disablePrevious}
      canNavigateNext={!disableNext}
      navigationLoading={isNavigationLoading}
      previousLabel="Previous Call"
      nextLabel="Next Call"
      closeLabel="Close"
    />
  );

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      width={width}
      closable={false}
      className="inbound-slide-out-drawer"
      title={title}
    >
      <div className="inbound-slide-out">
        <div className="call-details-section">
          <VoiceCallSummary callLog={callLog} variant="inbound" />
          <VoiceCallRecording callLog={callLog} callId={callLog?.id} isOutbound={false} variant="inbound" />

          {/* ENG-3107: renders itself to null when this call delivered no
              outbound result, or when the org is not entitled to the ENG-3061
              outcomes (the API omits result_delivery entirely). */}
          <ResultDeliveryPanel callLog={callLog} />

          {canUseCallFollowups && callLog?.database_call_log_id && (
            <CallFollowUpPanel
              callLog={callLog}
              onFollowUpLinked={(callFollowupId, sourceCallLog) => (
                onCallLogUpdated(sourceCallLog, { call_followup_id: callFollowupId })
              )}
            />
          )}

          <VoiceCallTranscript
            callLog={callLog}
            transcript={callLog?.transcript}
            variant="inbound"
          />
          <VoiceCallSid callSid={callLog?.call_sid} />
        </div>
      </div>
    </Drawer>
  );
};

InboundSlideOut.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  callLog: PropTypes.object,
  setCallIndex: PropTypes.func.isRequired,
  disableNext: PropTypes.bool,
  disablePrevious: PropTypes.bool,
  isNavigationLoading: PropTypes.bool,
  departments: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
  })),
  showDepartment: PropTypes.bool,
  onCallLogUpdated: PropTypes.func,
  timestampField: PropTypes.string,
};

InboundSlideOut.defaultProps = {
  callLog: null,
  disableNext: false,
  disablePrevious: false,
  isNavigationLoading: false,
  departments: [],
  showDepartment: false,
  onCallLogUpdated: () => {},
  timestampField: 'created_at',
};

export default InboundSlideOut;
export { getDepartmentName };
